import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { sendEmail } from '../../utils/helpers';
export function WeddingGuestsManager({ siteData, db }) {
    const [rsvps, setRsvps] = useState([]);
    const [stds, setStds] = useState([]);
    const [contributions, setContributions] = useState([]);

    const [guests, setGuests] = useState([]);
    const [newGuestName, setNewGuestName] = useState('');
    const [newGuestEmail, setNewGuestEmail] = useState('');
    const [newGuestPhone, setNewGuestPhone] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!siteData?.id) return;
        const unsubRsvps = onSnapshot(collection(db, `weddings/${siteData.id}/rsvps`), snap => {
            setRsvps(snap.docs.map(d => ({id: d.id, ...d.data()})));
        });
        const unsubStds = onSnapshot(collection(db, `weddings/${siteData.id}/saveTheDates`), snap => {
            setStds(snap.docs.map(d => ({id: d.id, ...d.data()})));
        });
        const unsubContribs = onSnapshot(collection(db, `weddings/${siteData.id}/contributions`), snap => {
            setContributions(snap.docs.map(d => ({id: d.id, ...d.data()})));
        });
        const unsubGuests = onSnapshot(collection(db, `weddings/${siteData.id}/guests`), snap => {
            setGuests(snap.docs.map(d => ({id: d.id, ...d.data()})));
        });
        return () => { unsubRsvps(); unsubStds(); unsubContribs(); unsubGuests(); };
    }, [siteData.id, db]);

    // Flatten party members so the planner can manage them as individual guests
    const flattenedGuests = useMemo(() => {
        const list = [];
        rsvps.forEach(r => {
            if (r.partyMembers) {
                r.partyMembers.forEach((m, idx) => {
                    list.push({
                        id: `${r.id}_${idx}`,
                        name: m.name,
                        email: m.email || '',
                        isAttending: m.isAttending,
                        mealChoice: m.mealChoice,
                        dietary: m.dietary,
                        submittedAt: r.submittedAt
                    });
                });
            } else {
                // Legacy support for older RSVPs
                list.push({
                    id: r.id,
                    name: r.guestName,
                    email: r.email || '',
                    isAttending: r.attending,
                    mealChoice: r.mealChoice,
                    dietary: '',
                    submittedAt: r.submittedAt
                });
                if (r.additionalFamily) {
                    r.additionalFamily.forEach((f, i) => {
                        list.push({
                            id: `${r.id}_fam_${i}`,
                            name: f.name,
                            isAttending: f.isAttending,
                            mealChoice: f.mealChoice || r.plusOneMealChoice || '',
                            dietary: '',
                            submittedAt: r.submittedAt
                        });
                    });
                }
            }
        });
        return list.sort((a,b) => (b.submittedAt?.seconds || 0) - (a.submittedAt?.seconds || 0));
    }, [rsvps]);

    const totalAttending = flattenedGuests.filter(g => g.isAttending).length;
    const totalDeclined = flattenedGuests.filter(g => !g.isAttending).length;

    const handleExportStds = () => {
        if (stds.length === 0) {
            alert("No addresses collected yet.");
            return;
        }
        const headers = "First Name,Last Name,Email,Phone,Additional Guests,Address 1,Address 2,City,State,Zip,Country\n";
        const csv = headers + stds.map(r => `"${r.firstName || ''}","${r.lastName || ''}","${r.email || ''}","${r.phone || ''}","${r.additionalGuests || 0}","${r.address1 || ''}","${r.address2 || ''}","${r.city || ''}","${r.state || ''}","${r.zip || ''}","${r.country || ''}"`).join("\n");
        
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", `${siteData.title.replace(/\s+/g, '_')}_Addresses.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleAddGuest = async () => {
        if (!newGuestName || (!newGuestEmail && !newGuestPhone)) {
            alert("Please enter a name and either an email or phone number.");
            return;
        }
        await addDoc(collection(db, `weddings/${siteData.id}/guests`), {
            name: newGuestName,
            email: newGuestEmail,
            phone: newGuestPhone,
            inviteSent: false,
            createdAt: serverTimestamp()
        });
        setNewGuestName('');
        setNewGuestEmail('');
        setNewGuestPhone('');
    };

    const handleRemoveGuest = async (id) => {
        if (window.confirm("Remove this guest from the invite list?")) {
            await deleteDoc(doc(db, `weddings/${siteData.id}/guests`, id));
        }
    };

    const handleSendSingleInvite = async (guest) => {
        if (!guest.email && !guest.phone) {
            alert("Please add an email or phone number for this guest first.");
            return;
        }
        if (!siteData.subdomain) {
            alert("Please set a custom URL (subdomain) in the Website Builder before sending invites.");
            return;
        }
        setSending(true);
        try {
            const publicUrl = `${window.location.origin}/w/${siteData.subdomain}`;
            const heroSection = siteData.sections?.find(s => s.type === 'hero')?.content || {};
            const heroImage = heroSection.imageUrl || siteData.generatedImage || null;
            
            // Clean phone number for Brevo SMS API
            let cleanPhone = null;
            if (guest.phone) {
                cleanPhone = guest.phone.replace(/[^\d+]/g, '');
                if (!cleanPhone.startsWith('+')) cleanPhone = '+1' + cleanPhone;
            }

            const imageHtml = heroImage ? `<img src="${heroImage}" alt="${siteData.title}" style="width: 100%; height: 280px; object-fit: cover; display: block; border-bottom: 1px solid #f3f4f6;" />` : '';

            const inviteHtml = `
                <div style="background-color: #f9fafb; padding: 60px 20px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">
                    <div style="max-width: 540px; margin: 0 auto; background-color: #ffffff; border-radius: 20px; overflow: hidden; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08); border: 1px solid #f3f4f6;">
                        ${imageHtml}
                        <div style="padding: 50px 40px; text-align: center;">
                            <span style="display: inline-block; padding: 6px 14px; background-color: #fdf2f8; color: #db2777; border-radius: 20px; font-size: 11px; font-weight: bold; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 24px;">You're Invited</span>
                            
                            <h1 style="margin: 0 0 24px 0; color: #111827; font-size: 36px; font-weight: 700; font-family: 'Georgia', serif; line-height: 1.2;">
                                ${siteData.title}
                            </h1>
                            
                            <p style="margin: 0 0 36px 0; color: #4b5563; font-size: 16px; line-height: 1.6; font-weight: 400;">
                                Hi ${guest.name},<br><br>We would be absolutely thrilled to have you join us. All the details you need are waiting for you on our wedding website.
                            </p>
                            
                            <a href="${publicUrl}" style="display: inline-block; background-color: #111827; color: #ffffff; text-decoration: none; padding: 16px 36px; border-radius: 50px; font-weight: 600; font-size: 15px; letter-spacing: 0.5px;">
                                View Details & RSVP
                            </a>
                            
                            <div style="margin-top: 40px; padding-top: 24px; border-top: 1px solid #f3f4f6;">
                                <p style="margin: 0; color: #9ca3af; font-size: 12px;">
                                    Trouble clicking? Paste this link into your browser:<br>
                                    <a href="${publicUrl}" style="color: #db2777; text-decoration: underline; word-break: break-all; margin-top: 6px; display: inline-block;">${publicUrl}</a>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div style="text-align: center; margin-top: 24px;">
                        <p style="color: #9ca3af; font-size: 12px; margin: 0;">Powered by <strong style="color: #db2777; font-style: italic;">Host-it!</strong></p>
                    </div>
                </div>
            `;

            const functions = getFunctions(db.app, 'us-central1');
            const sendBrevoNotification = httpsCallable(functions, 'sendBrevoNotification');
            
            // Generate a specifically short text format for SMS to prevent bouncing
            const shortSmsText = `Hi ${guest.name}, you're invited to ${siteData.title}! RSVP at: ${publicUrl}`;

            const response = await sendBrevoNotification({
                toEmail: guest.email || null,
                toPhone: cleanPhone,
                subject: `You're invited to ${siteData.title}!`,
                textContent: shortSmsText,
                htmlContent: inviteHtml
            });

            // Check the response to ensure at least one channel succeeded
            if (!response.data || !response.data.success) {
                throw new Error(response.data?.message || "Failed to send invitation via email/SMS");
            }
            
            const guestRef = doc(db, `weddings/${siteData.id}/guests`, guest.id);
            await updateDoc(guestRef, { inviteSent: true, inviteSentAt: serverTimestamp() });
            alert(`Invitation sent to ${guest.name}! (${response.data.message})`);
        } catch (err) {
            console.error("Error sending invitation:", err);
            alert(`Failed to send invitation: ${err.message}`);
        } finally {
            setSending(false);
        }
    };

    const handleSendInvites = async () => {
        const targetGuests = guests.filter(g => (g.email || g.phone) && !g.inviteSent);
        if (targetGuests.length === 0) {
            alert("No unsent guests found to invite. Add new guests with contact info first.");
            return;
        }
        if (!siteData.subdomain) {
            alert("Please set a custom URL (subdomain) in the Website Builder before sending invites.");
            return;
        }
        if (!window.confirm(`Send invitations to ${targetGuests.length} guests?`)) return;

        setSending(true);
        try {
            const publicUrl = `${window.location.origin}/w/${siteData.subdomain}`;
            const heroSection = siteData.sections?.find(s => s.type === 'hero')?.content || {};
            const heroImage = heroSection.imageUrl || siteData.generatedImage || null;
            const imageHtml = heroImage ? `<img src="${heroImage}" alt="${siteData.title}" style="width: 100%; height: 280px; object-fit: cover; display: block; border-bottom: 1px solid #f3f4f6;" />` : '';

            const functions = getFunctions(db.app, 'us-central1');
            const sendBrevoNotification = httpsCallable(functions, 'sendBrevoNotification');

            const results = [];
            const errors = [];

            const promises = targetGuests.map(async guest => {
                let cleanPhone = null;
                if (guest.phone) {
                    cleanPhone = guest.phone.replace(/[^\d+]/g, '');
                    if (!cleanPhone.startsWith('+')) cleanPhone = '+1' + cleanPhone;
                }

                const inviteHtml = `
                    <div style="background-color: #f9fafb; padding: 60px 20px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">
                        <div style="max-width: 540px; margin: 0 auto; background-color: #ffffff; border-radius: 20px; overflow: hidden; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08); border: 1px solid #f3f4f6;">
                            ${imageHtml}
                            <div style="padding: 50px 40px; text-align: center;">
                                <span style="display: inline-block; padding: 6px 14px; background-color: #fdf2f8; color: #db2777; border-radius: 20px; font-size: 11px; font-weight: bold; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 24px;">You're Invited</span>
                                
                                <h1 style="margin: 0 0 24px 0; color: #111827; font-size: 36px; font-weight: 700; font-family: 'Georgia', serif; line-height: 1.2;">
                                    ${siteData.title}
                                </h1>
                                
                                <p style="margin: 0 0 36px 0; color: #4b5563; font-size: 16px; line-height: 1.6; font-weight: 400;">
                                    Hi ${guest.name},<br><br>We would be absolutely thrilled to have you join us. All the details you need are waiting for you on our wedding website.
                                </p>
                                
                                <a href="${publicUrl}" style="display: inline-block; background-color: #111827; color: #ffffff; text-decoration: none; padding: 16px 36px; border-radius: 50px; font-weight: 600; font-size: 15px; letter-spacing: 0.5px;">
                                    View Details & RSVP
                                </a>
                                
                                <div style="margin-top: 40px; padding-top: 24px; border-top: 1px solid #f3f4f6;">
                                    <p style="margin: 0; color: #9ca3af; font-size: 12px;">
                                        Trouble clicking? Paste this link into your browser:<br>
                                        <a href="${publicUrl}" style="color: #db2777; text-decoration: underline; word-break: break-all; margin-top: 6px; display: inline-block;">${publicUrl}</a>
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div style="text-align: center; margin-top: 24px;">
                            <p style="color: #9ca3af; font-size: 12px; margin: 0;">Powered by <strong style="color: #db2777; font-style: italic;">Host-it!</strong></p>
                        </div>
                    </div>
                `;

                const shortSmsText = `Hi ${guest.name}, you're invited to ${siteData.title}! RSVP at: ${publicUrl}`;

                try {
                    const response = await sendBrevoNotification({
                        toEmail: guest.email || null,
                        toPhone: cleanPhone,
                        subject: `You're invited to ${siteData.title}!`,
                        textContent: shortSmsText,
                        htmlContent: inviteHtml
                    });

                    if (!response.data || !response.data.success) {
                        errors.push(`${guest.name}: ${response.data?.message || "Failed to send"}`);
                        return;
                    }

                    results.push(guest);
                    const guestRef = doc(db, `weddings/${siteData.id}/guests`, guest.id);
                    return updateDoc(guestRef, { inviteSent: true, inviteSentAt: serverTimestamp() });
                } catch (err) {
                    errors.push(`${guest.name}: ${err.message}`);
                }
            });

            await Promise.allSettled(promises);

            // Show results summary
            if (results.length === 0) {
                alert(`Failed to send any invitations. Errors:\n${errors.join("\n")}`);
            } else if (errors.length === 0) {
                alert(`✓ Successfully sent invitations to all ${results.length} guests!`);
            } else {
                alert(`✓ Sent to ${results.length}/${targetGuests.length} guests.\n\nFailed:\n${errors.join("\n")}`);
            }
        } catch (err) {
            console.error("Error sending invitations:", err);
            alert(`Failed to send invitations: ${err.message}`);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="p-4 md:p-8 h-full overflow-y-auto custom-scrollbar bg-gray-50">
            <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center">
                        <span className="text-4xl font-black text-emerald-500">{totalAttending}</span>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Attending</span>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center">
                        <span className="text-4xl font-black text-rose-500">{totalDeclined}</span>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Declined</span>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center">
                        <span className="text-4xl font-black text-blue-500">{stds.length}</span>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Addresses Saved</span>
                    </div>
                </div>

                {/* Guest List & Invitations */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-5 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                        <div>
                            <h3 className="font-bold text-lg text-gray-900">Guest List & Invitations</h3>
                            <p className="text-xs text-gray-500 mt-1">Add guests here to send them a direct link to your wedding website via email or text.</p>
                        </div>
                        <button onClick={handleSendInvites} disabled={sending} className="bg-pink-600 text-white px-5 py-2.5 rounded-lg font-bold hover:bg-pink-700 transition shadow-sm text-sm disabled:opacity-50 flex items-center gap-2">
                            {sending ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> : <span>💌</span>}
                            {sending ? 'Sending...' : 'Bulk Send Invites'}
                        </button>
                    </div>
                    <div className="p-4 bg-white border-b border-gray-100">
                        <div className="flex flex-col md:flex-row gap-2">
                            <input type="text" value={newGuestName} onChange={(e) => setNewGuestName(e.target.value)} placeholder="Guest Name" className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg flex-grow text-sm focus:ring-2 focus:ring-pink-500 outline-none" />
                            <input type="email" value={newGuestEmail} onChange={(e) => setNewGuestEmail(e.target.value)} placeholder="Email (Optional)" className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg flex-grow text-sm focus:ring-2 focus:ring-pink-500 outline-none" />
                            <input type="tel" value={newGuestPhone} onChange={(e) => setNewGuestPhone(e.target.value)} placeholder="Phone (Optional)" className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg w-full md:w-48 text-sm focus:ring-2 focus:ring-pink-500 outline-none" />
                            <button onClick={handleAddGuest} className="bg-gray-900 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-black transition shadow-sm text-sm h-[42px] md:h-auto">Add</button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-white border-b border-gray-200 text-xs uppercase font-bold text-gray-400 tracking-wider">
                                <tr>
                                    <th className="px-6 py-4">Guest Name</th>
                                    <th className="px-6 py-4">Contact Info</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {guests.map(guest => (
                                    <tr key={guest.id} className="hover:bg-gray-50 transition group">
                                        <td className="px-6 py-4 font-bold text-gray-900">{guest.name}</td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {guest.email && <span className="block truncate">{guest.email}</span>}
                                            {guest.phone && <span className="block text-xs">{guest.phone}</span>}
                                        </td>
                                        <td className="px-6 py-4">
                                            {guest.inviteSent ? (
                                                <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">Sent</span>
                                            ) : (
                                                <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">Not Sent</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right flex justify-end gap-2 items-center h-[72px]">
                                            <button 
                                                onClick={() => handleSendSingleInvite(guest)}
                                                disabled={sending || (!guest.email && !guest.phone)}
                                                className="bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold transition disabled:opacity-50 whitespace-nowrap"
                                            >
                                                {guest.inviteSent ? 'Resend' : 'Send Invite'}
                                            </button>
                                            <button onClick={() => handleRemoveGuest(guest.id)} className="text-red-400 hover:text-red-600 transition p-1.5 bg-white rounded-full shadow-sm border border-gray-100 opacity-0 group-hover:opacity-100">
                                                ✕
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {guests.length === 0 && <tr><td colSpan="4" className="px-6 py-12 text-center text-gray-500 italic">No guests added to the invite list yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Cash Fund Contributions */}
                {contributions.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                            <h3 className="font-bold text-lg text-gray-900">Cash Fund Contributions</h3>
                            <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                                Total Raised: ${contributions.reduce((acc, c) => acc + (c.amount || 0), 0).toFixed(2)}
                            </span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-white border-b border-gray-200 text-xs uppercase font-bold text-gray-400 tracking-wider">
                                    <tr>
                                        <th className="px-6 py-4">Guest Name</th>
                                        <th className="px-6 py-4">Fund</th>
                                        <th className="px-6 py-4">Amount</th>
                                        <th className="px-6 py-4">Message</th>
                                        <th className="px-6 py-4 text-right">Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {contributions.sort((a,b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)).map(c => (
                                        <tr key={c.id} className="hover:bg-gray-50 transition">
                                            <td className="px-6 py-4 font-bold text-gray-900">{c.guestName}</td>
                                            <td className="px-6 py-4 text-gray-600">{c.fundTitle}</td>
                                            <td className="px-6 py-4 font-bold text-emerald-600">${parseFloat(c.amount).toFixed(2)}</td>
                                            <td className="px-6 py-4 text-gray-500 italic max-w-xs truncate">{c.message || '-'}</td>
                                            <td className="px-6 py-4 text-gray-500 text-right">{c.createdAt?.seconds ? new Date(c.createdAt.seconds * 1000).toLocaleDateString() : 'Just now'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Website RSVPs */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                        <h3 className="font-bold text-lg text-gray-900">Website RSVPs</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-white border-b border-gray-200 text-xs uppercase font-bold text-gray-400 tracking-wider">
                                <tr>
                                    <th className="px-6 py-4">Guest Name</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Meal & Dietary</th>
                                    <th className="px-6 py-4 text-right">Date Submitted</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {flattenedGuests.map(g => (
                                    <tr key={g.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4">
                                            <p className="font-bold text-gray-900">{g.name}</p>
                                            {g.email && <p className="text-xs text-gray-500 mt-0.5"><a href={`mailto:${g.email}`} className="hover:text-pink-600 hover:underline">{g.email}</a></p>}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${g.isAttending ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                                {g.isAttending ? 'Attending' : 'Declined'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {g.mealChoice && <span className="block text-sm text-gray-800">{g.mealChoice}</span>}
                                            {g.dietary && <span className="block text-xs text-red-500 font-medium mt-0.5">{g.dietary}</span>}
                                            {!g.mealChoice && !g.dietary && <span className="text-gray-400 italic">-</span>}
                                        </td>
                                        <td className="px-6 py-4 text-gray-500 text-right">{g.submittedAt?.seconds ? new Date(g.submittedAt.seconds * 1000).toLocaleDateString() : 'Just now'}</td>
                                    </tr>
                                ))}
                                {flattenedGuests.length === 0 && <tr><td colSpan="4" className="px-6 py-12 text-center text-gray-500 italic">No RSVPs received via the website yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Save the Date Addresses */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                        <h3 className="font-bold text-lg text-gray-900">Save the Date Addresses</h3>
                        <button onClick={handleExportStds} className="text-sm bg-gray-900 text-white px-4 py-2 rounded-lg font-bold hover:bg-black transition shadow-sm flex items-center gap-2">
                            <span>⬇️</span> Export CSV
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-white border-b border-gray-200 text-xs uppercase font-bold text-gray-400 tracking-wider">
                                <tr>
                                    <th className="px-6 py-4">Name</th>
                                    <th className="px-6 py-4">Contact Info</th>
                                    <th className="px-6 py-4">Mailing Address</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {stds.map(s => (
                                    <tr key={s.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 font-bold text-gray-900 whitespace-nowrap">
                                            {s.firstName} {s.lastName}
                                            {s.additionalGuests > 0 && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">+{s.additionalGuests}</span>}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            <a href={`mailto:${s.email}`} className="text-pink-600 hover:underline">{s.email}</a><br/><span className="text-xs">{s.phone}</span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {s.address1} {s.address2}<br/>
                                            {s.city}, {s.state} {s.zip}
                                        </td>
                                    </tr>
                                ))}
                                {stds.length === 0 && <tr><td colSpan="3" className="px-6 py-12 text-center text-gray-500 italic">No mailing addresses collected yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ==========================================
// 2. RSVP SECTION (Builder & Public View)
// ==========================================
export function RsvpSection({ content, theme, weddingId, isEditing, db }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    
    const [partyMembers, setPartyMembers] = useState([
        { id: Date.now(), name: '', email: '', isAttending: 'yes', mealChoice: '', dietary: '' }
    ]);
    const [submitted, setSubmitted] = useState(false);

    const isPastDeadline = content.deadline && new Date(content.deadline) < new Date(new Date().setHours(0,0,0,0));
    const maxPartySize = content.maxPartySize || 10;
    const canAddMore = partyMembers.length < maxPartySize;

    const handleAddMember = () => {
        setPartyMembers([...partyMembers, { id: Date.now(), name: '', email: '', isAttending: 'yes', mealChoice: '', dietary: '' }]);
    };

    const handleMemberChange = (id, field, value) => {
        setPartyMembers(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m));
    };

    const handleRemoveMember = (id) => {
        setPartyMembers(prev => prev.filter(m => m.id !== id));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Ensure all members have names
        if (partyMembers.some(m => !m.name.trim())) return;
        
        try {
            await addDoc(collection(db, `weddings/${weddingId}/rsvps`), {
                partyMembers: partyMembers.map(m => ({
                    name: m.name.trim(),
                    email: m.email ? m.email.trim() : '',
                    isAttending: m.isAttending === 'yes',
                    mealChoice: m.mealChoice || '',
                    dietary: m.dietary || ''
                })),
                submittedAt: serverTimestamp()
            });
            setSubmitted(true);
        } catch (err) {
            console.error("RSVP Error:", err);
            alert("Failed to submit RSVP. Please try again.");
        }
    };

    return (
        <div className="py-24 border-t-8 border-transparent" style={{ backgroundColor: content.bgColor || theme.backgroundColor, borderTopColor: theme.primaryColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto p-4 md:p-8 max-w-2xl bg-white shadow-2xl rounded-3xl border border-gray-100 relative overflow-hidden text-gray-900">
                <div className="absolute top-0 left-0 w-full h-2" style={{ backgroundColor: theme.primaryColor }}></div>
                <h2 className={`${sizes.heading} font-bold text-center mb-8 font-serif`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                {submitted ? (
                    <div className="text-center py-12 animate-fade-in">
                        <div className="text-5xl mb-4">✨</div>
                        <p className={`font-bold text-gray-900 ${sizes.cardHeading}`}>Thank you!</p>
                        <p className={`text-gray-600 mt-2 ${sizes.body}`}>Your response has been received.</p>
                    </div>
                ) : isPastDeadline && !isEditing ? (
                    <div className="text-center py-12 animate-fade-in bg-gray-50 rounded-2xl border border-gray-200 m-4">
                        <div className="text-5xl mb-4">⌛</div>
                        <p className={`font-bold text-gray-900 ${sizes.cardHeading}`}>RSVPs are closed</p>
                        <p className={`text-gray-600 mt-2 ${sizes.body}`}>The deadline to RSVP was {new Date(content.deadline).toLocaleDateString()}. Please contact the host directly if you need to make changes.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        
                        <div className="space-y-6">
                            {partyMembers.map((member, index) => (
                                <div key={member.id} className="bg-gray-50 p-5 rounded-2xl border border-gray-200 relative animate-fade-in">
                                    {index > 0 && (
                                        <button type="button" onClick={() => handleRemoveMember(member.id)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 font-bold text-lg leading-none transition-colors">✕</button>
                                    )}
                                    <h4 className={`font-bold text-gray-800 mb-4 uppercase tracking-wider ${sizes.small}`}>Guest {index + 1}</h4>
                                    
                                    <div className="space-y-4">
                                        <input 
                                            type="text" 
                                            required 
                                            value={member.name} 
                                            onChange={e => handleMemberChange(member.id, 'name', e.target.value)} 
                                            placeholder="Full Name *" 
                                            className={`w-full p-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition ${sizes.body}`} 
                                            style={{ '--tw-ring-color': theme.primaryColor }} 
                                        />
                                        <input 
                                            type="email" 
                                            value={member.email} 
                                            onChange={e => handleMemberChange(member.id, 'email', e.target.value)} 
                                            placeholder="Email Address (Optional)" 
                                            className={`w-full p-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition ${sizes.body}`} 
                                            style={{ '--tw-ring-color': theme.primaryColor }} 
                                        />
                                        
                                        <div className="flex gap-4 items-center">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="radio" name={`attending-${member.id}`} checked={member.isAttending === 'yes'} onChange={() => handleMemberChange(member.id, 'isAttending', 'yes')} className="w-5 h-5 text-pink-600 focus:ring-pink-500" />
                                                <span className={`font-medium ${sizes.body}`}>Joyfully Accept</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="radio" name={`attending-${member.id}`} checked={member.isAttending === 'no'} onChange={() => handleMemberChange(member.id, 'isAttending', 'no')} className="w-5 h-5 text-pink-600 focus:ring-pink-500" />
                                                <span className={`font-medium text-gray-600 ${sizes.body}`}>Regretfully Decline</span>
                                            </label>
                                        </div>

                                        {member.isAttending === 'yes' && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-in">
                                                <input 
                                                    type="text" 
                                                    value={member.mealChoice} 
                                                    onChange={e => handleMemberChange(member.id, 'mealChoice', e.target.value)} 
                                                    placeholder="Meal Preference (e.g. Chicken, Vegan)" 
                                                    className={`w-full p-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition ${sizes.body}`} 
                                                    style={{ '--tw-ring-color': theme.primaryColor }} 
                                                />
                                                <input 
                                                    type="text" 
                                                    value={member.dietary} 
                                                    onChange={e => handleMemberChange(member.id, 'dietary', e.target.value)} 
                                                    placeholder="Dietary restrictions / Allergies" 
                                                    className={`w-full p-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition ${sizes.body}`} 
                                                    style={{ '--tw-ring-color': theme.primaryColor }} 
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {canAddMore && (
                            <div className="pt-2">
                                <button type="button" onClick={handleAddMember} className="font-bold flex items-center justify-center gap-2 w-full py-4 rounded-xl border-2 border-dashed bg-gray-50 hover:bg-gray-100 transition-colors text-gray-600" style={{ borderColor: theme.primaryColor, color: theme.primaryColor }}>
                                    <span className="text-xl leading-none">+</span> Add another guest to this RSVP
                                </button>
                            </div>
                        )}

                        <button type="submit" className={`w-full text-white py-4 rounded-xl font-bold hover:shadow-lg transform hover:-translate-y-1 transition-all mt-6 shadow-md ${sizes.body}`} style={{backgroundColor: theme.primaryColor}}>Submit RSVP</button>
                    </form>
                )}
            </div>
        </div>
    );
}

// ==========================================
// 3. SAVE THE DATE MODAL
// ==========================================
export function SaveTheDateModal({ siteData, onClose, db }) {
    const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', additionalGuests: 0, address1: '', address2: '', city: '', state: '', zip: '', country: 'US' });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const theme = siteData.theme || {};
    const primaryColor = theme.primaryColor || '#ec4899';
    const fontFamily = theme.fontFamily || 'inherit';

    const heroSection = siteData.sections.find(s => s.type === 'hero')?.content || {};
    const eventDate = heroSection.date || '';
    const eventTitle = siteData.title || 'Our Wedding';
    const heroImage = heroSection.imageUrl || null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === 'additionalGuests' ? (parseInt(value) || 0) : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.firstName || !formData.lastName || !formData.email) return;
        setLoading(true);
        try {
            await addDoc(collection(db, `weddings/${siteData.id}/saveTheDates`), {
                ...formData,
                createdAt: serverTimestamp()
            });
            setSubmitted(true);
        } catch(err) {
            console.error("Save The Date Error:", err);
            alert("Failed to submit details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 sm:p-6 animate-fade-in overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col md:flex-row overflow-hidden relative min-h-[500px] md:h-auto my-8 md:my-0">
                <button onClick={onClose} className="absolute top-3 right-3 md:top-4 md:right-4 z-[60] bg-white/90 hover:bg-white backdrop-blur-md rounded-full w-10 h-10 flex items-center justify-center text-gray-900 transition-colors shadow-md font-bold border border-gray-200 text-xl">×</button>
                
                {/* Left Side Image Panel */}
                <div className="w-full md:w-2/5 bg-gray-100 relative flex-shrink-0 min-h-[250px] md:min-h-full">
                    {heroImage ? (
                        <img src={heroImage} alt="Event Theme" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                        <div className="absolute inset-0 w-full h-full" style={{ backgroundColor: primaryColor, opacity: 0.2 }}></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-8 text-white">
                        <h3 className="text-3xl font-bold font-serif mb-2" style={{ fontFamily }}>{eventTitle}</h3>
                        {eventDate && <p className="text-lg font-medium opacity-90">{new Date(eventDate.replace(/-/g, '/')).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>}
                        <p className="text-xs opacity-70 mt-2 uppercase tracking-widest font-bold">Save the Date</p>
                    </div>
                </div>

                {/* Right Side Form Panel */}
                <div className="w-full md:w-3/5 p-6 md:p-10 bg-white flex flex-col justify-center">
                    {submitted ? (
                        <div className="text-center animate-fade-in space-y-6 py-8">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto text-4xl text-green-500 shadow-inner">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <div>
                                <h3 className="text-3xl font-bold text-gray-900 font-serif mb-2" style={{ fontFamily }}>Thank you!</h3>
                                <p className="text-gray-600">We've securely saved your details. Formal invitation to follow.</p>
                            </div>
                            <button onClick={onClose} className="mt-6 text-gray-500 hover:text-gray-900 font-bold transition underline text-sm">Return to Website</button>
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2 font-serif" style={{ fontFamily }}>Let's stay in touch!</h3>
                                <p className="text-sm text-gray-600">Please provide your mailing address and email so we can send you updates and a formal invitation.</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">First Name *</label>
                                        <input type="text" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Last Name *</label>
                                        <input type="text" name="lastName" required value={formData.lastName} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Email Address *</label>
                                        <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Phone Number</label>
                                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                </div>

                                {siteData.allowAdditionalGuests && (
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Additional Guests (+{siteData.maxAdditionalGuests || 1} Max)</label>
                                        <input type="number" name="additionalGuests" min="0" max={siteData.maxAdditionalGuests || 1} value={formData.additionalGuests} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                )}

                                <div className="pt-3 border-t border-gray-100">
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Mailing Address (For Formal Invite)</label>
                                    <div className="space-y-2">
                                        <input type="text" name="address1" placeholder="Street Address" value={formData.address1} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                        <input type="text" name="address2" placeholder="Apt, Suite, Unit (optional)" value={formData.address2} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                        <div className="grid grid-cols-3 gap-2">
                                            <input type="text" name="city" placeholder="City" value={formData.city} onChange={handleChange} className="col-span-3 sm:col-span-1 p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                            <input type="text" name="state" placeholder="State" value={formData.state} onChange={handleChange} className="col-span-1 p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                            <input type="text" name="zip" placeholder="Zip Code" value={formData.zip} onChange={handleChange} className="col-span-2 sm:col-span-1 p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 flex flex-col gap-2">
                                    <button type="submit" disabled={loading} className="w-full py-4 rounded-xl font-bold text-white shadow-md hover:opacity-90 transform hover:-translate-y-0.5 transition-all disabled:opacity-50" style={{ backgroundColor: primaryColor }}>
                                        {loading ? 'Saving...' : 'Submit & Save Date'}
                                    </button>
                                    <button type="button" onClick={onClose} className="w-full py-3 rounded-xl font-bold text-gray-500 hover:text-gray-800 transition text-sm">
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// Sizing Classes Helper
const getSizingClasses = (textSize) => {
    switch(textSize) {
        case 'small': return { 
            heroHeading: 'text-4xl md:text-5xl lg:text-6xl', 
            heroSub: 'text-base md:text-xl', 
            heading: 'text-3xl md:text-4xl', 
            body: 'text-base', 
            small: 'text-xs', 
            cardHeading: 'text-lg md:text-xl' 
        };
        case 'large': return { 
            heroHeading: 'text-6xl md:text-8xl lg:text-9xl', 
            heroSub: 'text-xl md:text-3xl', 
            heading: 'text-5xl md:text-6xl', 
            body: 'text-xl md:text-2xl', 
            small: 'text-base', 
            cardHeading: 'text-3xl md:text-4xl' 
        };
        case 'xlarge': return { 
            heroHeading: 'text-7xl md:text-9xl lg:text-[10rem]', 
            heroSub: 'text-2xl md:text-4xl', 
            heading: 'text-6xl md:text-7xl', 
            body: 'text-2xl md:text-3xl', 
            small: 'text-lg', 
            cardHeading: 'text-4xl md:text-5xl' 
        };
        default: return { 
            heroHeading: 'text-5xl md:text-7xl lg:text-8xl', 
            heroSub: 'text-lg md:text-2xl', 
            heading: 'text-4xl md:text-5xl', 
            body: 'text-lg', 
            small: 'text-sm', 
            cardHeading: 'text-2xl md:text-3xl' 
        };
    }
};